import { useEffect, useState, useMemo, useCallback } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import api from "../services/api"
import { isAdminIpDenied, getAdminIpDeniedMessage, ADMIN_IP_DENIED_MESSAGE } from "../utils/adminIpError"

export default function AdminLayout({ children, title, description }) {
    const navigate = useNavigate()
    const location = useLocation()
    const [user, setUser] = useState(null)
    const [loading, setLoading] = useState(true)
    const [pendingCount, setPendingCount] = useState(0)
    const [ipDenied, setIpDenied] = useState(false)
    const [ipDeniedMessage, setIpDeniedMessage] = useState(ADMIN_IP_DENIED_MESSAGE)

    const fetchPendingCount = useCallback(async () => {
        try {
            const res = await api.get("/admin/access-requests?status=pending&limit=1")
            setPendingCount(res.data.counts?.pending || 0)
        } catch {
            setPendingCount(0)
        }
    }, [])

    useEffect(() => {
        let cancelled = false
        async function verifySession() {
            try {
                const res = await api.get("/auth/me")
                if (cancelled) return
                const serverUser = res.data
                if (serverUser.role !== "admin") {
                    localStorage.removeItem("user")
                    navigate("/")
                    return
                }
                localStorage.setItem("user", JSON.stringify(serverUser))
                setUser(serverUser)
                setIpDenied(false)
                fetchPendingCount()
            } catch (err) {
                if (cancelled) return
                localStorage.removeItem("user")
                if (isAdminIpDenied(err)) {
                    setIpDenied(true)
                    setIpDeniedMessage(getAdminIpDeniedMessage(err))
                    setUser(null)
                } else {
                    navigate("/")
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        }
        verifySession()
        return () => { cancelled = true }
    }, [navigate, fetchPendingCount])

    useEffect(() => {
        const onIpDenied = (event) => {
            setIpDenied(true)
            setIpDeniedMessage(event.detail?.message || ADMIN_IP_DENIED_MESSAGE)
            setUser(null)
            localStorage.removeItem("user")
        }
        window.addEventListener("admin-ip-denied", onIpDenied)
        return () => window.removeEventListener("admin-ip-denied", onIpDenied)
    }, [])

    useEffect(() => {
        if (!user) return
        const interval = setInterval(fetchPendingCount, 60000)
        return () => clearInterval(interval)
    }, [user, fetchPendingCount])

    const handleLogout = async () => {
        try { await api.post("/auth/logout") } catch (_) {}
        localStorage.removeItem("user")
        navigate("/")
    }

    const handleBackToTrustCenter = async () => {
        try { await api.post("/auth/logout") } catch (_) {}
        localStorage.removeItem("user")
        navigate("/")
    }

    const perms = useMemo(() => user?.permissions ?? {}, [user])
    // Intentionally does NOT require viewAuditLog — that permission is new and
    // existing full-access admins (created before it existed) must not lose
    // "super admin" status just because that one field isn't set yet.
    const isSuper = user?.role === "admin" && perms.approveRequests && perms.uploadDocs && perms.manageAdmins && perms.manageWidgets

    const navItems = useMemo(() => [
        { label: "Access Requests", path: "/admin", active: location.pathname === "/admin", show: isSuper || perms.approveRequests, badge: pendingCount > 0 ? pendingCount : null },
        { label: "Signed NDAs", path: "/admin/ndas", active: location.pathname === "/admin/ndas", show: isSuper || perms.approveRequests },
        { label: "Security Controls", path: "/widgets", active: location.pathname === "/widgets", show: isSuper || perms.manageWidgets },
        { label: "Manage Admins", path: "/admins", active: location.pathname === "/admins", show: isSuper || perms.manageAdmins },
        { label: "Upload Docs", path: "/upload", active: location.pathname === "/upload", show: isSuper || perms.uploadDocs },
        { label: "Login History", path: "/login-history", active: location.pathname === "/login-history", show: isSuper },
        { label: "Audit Log", path: "/audit-log", active: location.pathname === "/audit-log", show: isSuper || perms.viewAuditLog },
        { label: "Settings", path: "/settings", active: location.pathname === "/settings", show: isSuper }
    ].filter(item => item.show), [location.pathname, isSuper, perms, pendingCount])

    useEffect(() => {
        if (loading || !user) return
        const currentPathAllowed = navItems.some(item => item.path === location.pathname)
        if (!currentPathAllowed && !isSuper) {
            if (navItems.length > 0) {
                navigate(navItems[0].path)
            } else {
                navigate("/")
            }
        }
    }, [location.pathname, user, navItems, isSuper, navigate, loading])

    if (loading) {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", color: "#6B778C" }}>
                Verifying access…
            </div>
        )
    }

    if (ipDenied) {
        return (
            <div style={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "24px",
                background: "#f5f7fa",
                fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
            }}>
                <div style={{
                    maxWidth: 480,
                    width: "100%",
                    background: "#fff",
                    border: "1px solid #dde2ea",
                    borderRadius: 16,
                    padding: "32px 28px",
                    boxShadow: "0 8px 28px rgba(12,35,64,0.08)",
                    textAlign: "center",
                }}>
                    <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        background: "#fff4eb",
                        color: "#F4811F",
                        display: "grid",
                        placeItems: "center",
                        margin: "0 auto 16px",
                        fontSize: 12,
                        fontWeight: 800,
                        letterSpacing: "0.04em",
                    }}>
                        IP
                    </div>
                    <h1 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 800, color: "#002B54" }}>
                        Admin access blocked
                    </h1>
                    <p style={{ margin: "0 0 24px", fontSize: 14, lineHeight: 1.55, color: "#64748b" }}>
                        {ipDeniedMessage}
                    </p>
                    <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                        <button
                            type="button"
                            onClick={handleBackToTrustCenter}
                            style={{
                                padding: "10px 18px",
                                borderRadius: 8,
                                border: "none",
                                background: "linear-gradient(135deg, #002B54, #1e4070)",
                                color: "#fff",
                                fontSize: 13,
                                fontWeight: 700,
                                cursor: "pointer",
                            }}
                        >
                            Back to Trust Center
                        </button>
                        <button
                            type="button"
                            onClick={() => window.location.reload()}
                            style={{
                                padding: "10px 18px",
                                borderRadius: 8,
                                border: "1px solid #dde2ea",
                                background: "#fff",
                                color: "#002B54",
                                fontSize: 13,
                                fontWeight: 700,
                                cursor: "pointer",
                            }}
                        >
                            Try again
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    if (!user) return null

    return (
        <div className="admin-layout">
            <aside className="admin-sidebar">
                <div className="admin-sidebar-header">
                    <div className="admin-sidebar-logo" style={{ fontFamily: "'DarwinSans', system-ui, sans-serif" }}>
                        <span style={{ color: '#66B8FF' }}>Trust</span> Admin
                    </div>
                    {/* rgba(255,255,255,0.65) on --navy meets WCAG AA (~4.5:1) for small text; 0.4 measured ~3.5:1 and failed */}
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.65)', marginTop: '4px', fontWeight: 500, letterSpacing: '0.02em' }}>
                        Admin Panel
                    </div>
                </div>

                <nav className="admin-nav">
                    {navItems.map(item => (
                        <button
                            key={item.label}
                            className={`admin-nav-item ${item.active ? 'active' : ''}`}
                            onClick={() => navigate(item.path)}
                            aria-current={item.active ? 'page' : undefined}
                            style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                        >
                            <span>{item.label}</span>
                            {item.badge && (
                                <span
                                    className="admin-nav-badge"
                                    style={{
                                        minWidth: '20px',
                                        height: '20px',
                                        padding: '0 6px',
                                        borderRadius: '10px',
                                        background: '#ef4444',
                                        color: '#fff',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginLeft: '8px',
                                    }}
                                >
                                    {item.badge > 99 ? '99+' : item.badge}
                                </span>
                            )}
                        </button>
                    ))}
                </nav>
                <style>{`
                    @keyframes pulse-badge {
                        0%, 100% { transform: scale(1); }
                        50% { transform: scale(1.1); }
                    }
                    .admin-nav-badge {
                        animation: pulse-badge 2s infinite;
                    }
                    @media (prefers-reduced-motion: reduce) {
                        .admin-nav-badge {
                            animation: none;
                        }
                    }
                `}</style>

                <div className="admin-sidebar-footer">
                    <button className="admin-nav-item" onClick={() => navigate("/")}>
                        ← Back to Trust Center
                    </button>
                    <button className="admin-nav-item text-red-600" onClick={handleLogout} style={{ marginTop: "8px" }}>
                        Logout
                    </button>
                </div>
            </aside>

            <main className="admin-main">
                <header className="admin-page-header">
                    <h1 className="admin-page-title">{title}</h1>
                    {description && <p className="admin-page-desc">{description}</p>}
                </header>

                <div className="admin-content">
                    {children}
                </div>
            </main>
        </div>
    )
}
